import { useState, useEffect } from "react";
import { Col, Container, Row } from "reactstrap";
import UserProfileCard from "./UserProfileCard";
import Loader from "../../../components/Loader";
import { IUser, UserRole } from "../../../models";
import EmailChangeCard from "./EmailChangeCard";
import SupportAccessCard from "./SupportAccessCard";
import { getIsSupportImpersonationActive } from "../../../services/userImpersonation";
import { useIsConnectForSpreadsheets } from "../../../hooks/useIsConnectForSpreadsheets";
import { useAppSelector } from "../../../redux/hooks";
import { useQuery } from "@tanstack/react-query";
import { getSelf } from "src/api/Users/getSelf";
import { getIsAccountUsingSSO } from "src/api/Accounts/getIsAccountUsingSSO";
import Page500 from "src/pages/auth/Page500";

const UserProfileTab = () => {
  const user = useAppSelector((u) => u.user);
  const isOemAdmin =
    user.role === UserRole.OEMAdmin || user.role === UserRole.ServiceUser;
  const isOemSub = user.role === UserRole.ServiceUser;
  const isSupportImpersonationActive = getIsSupportImpersonationActive();
  const isSpreadsheetUser = useIsConnectForSpreadsheets();

  const [userInfo, setUserInfo] = useState<IUser | undefined>(undefined);

  const {
    data: userData,
    isLoading: isLoadingUser,
    error: selfError,
  } = useQuery({
    queryKey: ["/users/self"],
    // Disable impersonation for OEM admins that are impersonating a sub-account, they can get here but it is their own user, not the sub-account.
    queryFn: () => getSelf({ disableImpersonation: isOemSub }),
    meta: {
      errorMessage: "Error loading user data",
    },
  });

  useEffect(() => {
    setUserInfo(userData);
  }, [userData]);

  const { data: isAccountUsingSSO, isLoading: isLoadingSSO } = useQuery({
    queryKey: ["/account/isAccountUsingSSO"],
    queryFn: () => getIsAccountUsingSSO({ disableImpersonation: isOemSub }),
    meta: {
      errorMessage: "Error loading SSO status for account",
    },
  });

  if (isLoadingUser || isLoadingSSO || !userInfo) {
    return (
      <Container fluid className="p-0">
        <Loader />
      </Container>
    );
  }

  // We don't really care if the SSO status API call fails, it only affects a single field.
  if (selfError) {
    return <Page500 error={selfError} />;
  }

  const initialFormValues = {
    LoginEmail: userInfo.pendingEmail ?? userInfo.email!,
  };

  return (
    <Container fluid className="p-0">
      <Row>
        <Col>
          <UserProfileCard
            isSupportImpersonationActive={isSupportImpersonationActive}
          />
          <EmailChangeCard
            initialValues={initialFormValues}
            userInfo={userInfo}
            setUserInfo={setUserInfo}
            isSSO={isAccountUsingSSO ?? false}
            isSupportImpersonationActive={isSupportImpersonationActive}
          />
          {!isOemAdmin && !isSpreadsheetUser && (
            <SupportAccessCard
              userInfo={userInfo}
              setUserInfo={setUserInfo}
              isSupportImpersonationActive={isSupportImpersonationActive}
            />
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default UserProfileTab;
